import { Form, Input, Typography, message, Button, Space, Row, Col, theme, Select, Alert } from 'antd'
import React, { useCallback, useEffect, useState } from 'react'
import { getFeegowIntegration } from '../../../../../services/feegow-integration'
import { createTask, getTasks } from '../../../../../services/tasks'
import { getMyZapInstance } from "../../../../../services/myZap";
import { getEvolutionInstance } from "../../../../../services/evolution";
import { getWhatsappInstance } from "../../../../../services/whatsappInstances";
import { SaveOutlined, LinkOutlined, ClockCircleOutlined, MessageOutlined } from '@ant-design/icons'

const { Text } = Typography

export default function FeegowNotificationTasks({ assistantId, onCancel, onSuccess }) {
  const { token } = theme.useToken()
  const [loading, setLoading] = useState(false)
  const [hasIntegration, setHasIntegration] = useState(false)
  const [saving, setSaving] = useState(false)
  const [instances, setInstances] = useState([])
  const [alreadyExists, setAlreadyExists] = useState(false)
  const [form] = Form.useForm()

  const contentStyle = {
    textAlign: 'center',
    color: token.colorTextTertiary,
    borderRadius: token.borderRadiusLG,
    border: `1px dashed ${token.colorBorder}`,
    marginTop: 16,
    padding: '24px 12px',
    maxHeight: '400px',
    overflowY: 'auto'
  };

  useEffect(() => {
    setLoading(true)
    const collectedInstances = []

    Promise.all([
      getFeegowIntegration({ assistantID: assistantId }).then(({ data }) => {
        setHasIntegration(data && data.length > 0)
      }),
      getTasks(assistantId).then(({ data }) => {
        const exists = (data || []).some(t => t.type === 'FEEGOW_REMINDER')
        setAlreadyExists(exists)
      }),
      getMyZapInstance({assistantID: assistantId}).then(({data}) => {
          if (data && data.length > 0) collectedInstances.push({type: "MY_ZAP", id: data[0].id, name: "MyZap"})
      }),
      getEvolutionInstance({assistantID: assistantId}).then(({data}) => {
          if (data && data.length > 0) collectedInstances.push({type: "EVOLUTION", id: data[0].id, name: "Evolution"})
      }),
      getWhatsappInstance({assistantID: assistantId}).then(({data}) => {
          if (data && data.length > 0) collectedInstances.push({type: "WHATSAPP", id: data[0].id, name: "WhatsApp Oficial"})
      }).catch(() => {})
    ])
    .then(() => {
      setInstances(collectedInstances)
    })
    .catch(console.error)
    .finally(() => setLoading(false))
  }, [assistantId])

  useEffect(() => {
    if (instances.length === 1) {
      form.setFieldValue('integrationID', instances[0].id)
    }
  }, [instances, form])

  const finishForm = useCallback(async () => {
    try {
      const values = await form.validateFields()
      setSaving(true)

      const payload = {
        assistantID: parseInt(assistantId, 10),
        integrationType: instances.find(i => i.id === values.integrationID)?.type || "WHATSAPP",
        integrationID: parseInt(values.integrationID, 10),
        type: 'FEEGOW_REMINDER',
        triggerType: 'CRONJOB',
        triggerData: '* * * * *', // Sempre a cada minuto por padrão
        data: {
          hoursBefore: parseInt(values.hoursBefore, 10),
          templateName: values.templateName
        }
      }

      await createTask(assistantId, payload)
      message.success('Tarefa de Lembrete criada com sucesso!')
      if (onSuccess) onSuccess()
    } catch (error) {
      console.error(error)
      message.error('Erro ao criar a tarefa. Verifique os campos.')
    } finally {
      setSaving(false)
    }
  }, [assistantId, form, instances, onSuccess])

  if (loading) return <div style={{ minHeight: 200 }} />
  if (!hasIntegration) return null

  return (
    <>
      <div style={contentStyle}>
        <div style={{ textAlign: 'left', maxWidth: 600, margin: '0 auto' }}>
          <Space align="center" size="middle" style={{ marginBottom: 24 }}>
            <div style={{ color: token.colorPrimary, display: 'flex' }}>
              <LinkOutlined style={{ fontSize: 24 }} />
            </div>
            <div>
              <Typography.Title level={5} style={{ margin: 0, color: '#262626' }}>
                Novo Lembrete Feegow
              </Typography.Title>
              <Text type="secondary" style={{ fontSize: 13 }}>
                Configure um novo gatilho automático de lembrete via WhatsApp.
              </Text>
            </div>
          </Space>

          {alreadyExists && (
            <Alert
              message="Tarefa já existente"
              description="Este assistente já possui uma tarefa de lembrete Feegow ativa. Delete a anterior na lista de tarefas antes de criar uma nova."
              type="error"
              showIcon
              style={{ marginBottom: 24 }}
            />
          )}

          <Form 
            layout={'vertical'} 
            form={form} 
            disabled={alreadyExists}
            initialValues={{ hoursBefore: 24, templateName: 'feegow_appointment_confirmation' }}
          >
            <div style={{ backgroundColor: '#f8f9fa', padding: '20px', borderRadius: 8, border: '1px dashed #d9d9d9' }}>
              <Row gutter={24}>
                <Col xs={24} md={24}>
                  <Form.Item
                    label={<Text strong>Antecedência (Horas)</Text>}
                    help={'Ex: Se configurar "24", o robô vigiará a agenda e avisará o paciente exatamente 24h antes do horário da consulta.'}
                    name={'hoursBefore'}
                    rules={[{ required: true, message: 'Obrigatório' }]}
                  >
                    <Input type="number" placeholder={'Ex: 24'} size="large" />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={24} style={{ marginTop: 16 }}>
                <Col xs={24} md={instances.length > 1 ? 12 : 24}>
                  <Form.Item
                    label={
                      <Space>
                        <MessageOutlined />
                        <Text strong>Nome do Template (WhatsApp)</Text>
                      </Space>
                    }
                    help={'O nome exato do template aprovado na Meta.'}
                    name={'templateName'}
                    rules={[{ required: true, message: 'Obrigatório' }]}
                  >
                    <Input placeholder={'Ex: lembrete_consulta'} size="large" />
                  </Form.Item>
                </Col>

                <Col xs={24} md={instances.length > 1 ? 12 : 24} style={{ display: instances.length === 1 ? 'none' : 'block' }}>
                  <Form.Item
                    label={<Text strong>Instância de WhatsApp</Text>}
                    name={'integrationID'}
                    rules={[{ required: true, message: 'Selecione a instância' }]}
                  >
                    <Select size="large" placeholder="Selecione a conexão">
                      {instances.map(inst => (
                        <Select.Option key={inst.id} value={inst.id}>
                          {inst.name} (ID: {inst.id})
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
            </div>
          </Form>
        </div>
      </div>

      <div style={{ marginTop: 24 }}>
        <Row justify="end">
          <Space>
            {onCancel && <Button onClick={onCancel}>Cancelar</Button>}
            <Button 
              type="primary" 
              onClick={finishForm} 
              loading={saving} 
              disabled={alreadyExists} 
              icon={<SaveOutlined />}
            >
              Criar Tarefa de Lembrete
            </Button>
          </Space>
        </Row>
      </div>
    </>
  )
}
